const isEmpty = require('lodash/isEmpty');
const ResponseError = require('./ResponseError');
const pathToRegexp = require('path-to-regexp');
const querystring = require('query-string');
const Ajv = require('ajv');
const { fetch } = require('./get-fetch');

const ajvFactory = require('./ajv-factory');

const httpMethods = { getJSON, postJSON, patchJSON, _delete, putJSON };
const getMethod = (method) => httpMethods[method] || getJSON;

module.exports = function client(serviceInfo, { api, schemas }, auth) {
  const { uri, token } = getServiceInfo(serviceInfo);
  const ajv = ajvFactory.create(schemas);
  return Object.keys(api || {}).reduce((service, key) => {
    service[key] = serviceCall(api[key], { auth, token }, ajv);
    service[key].proxy = realProxy({ ...api[key], token });
    return service;
  }, {});

  function realProxy({ path, token }) {
    return async (req, res) => {
      try {
        let entryServiceUrl = req.get('x-entry-service');
        if (!entryServiceUrl) {
          const { host, protocol, originalUrl } = req;
          entryServiceUrl = `${protocol}://${host}${originalUrl}`;
        }

        const headers = {
          'user-agent': req.headers['user-agent'],
          cookie: req.headers.cookie,
          'x-entry-service': entryServiceUrl,
          authorization: token,
          'Content-Type': 'application/json',
        };

        if (req.headers['x-multifamily']) {
          headers['x-multifamily'] = req.headers['x-multifamily'];
        }

        const response = await fetch(formatUrl(path, req), {
          body: req.method === 'GET' || req.method === 'HEAD' ? undefined : JSON.stringify(req.body),
          headers,
          method: req.method,
        });
        res.status(response.status);
        for (const [header, value] of response.headers) {
          res.set(header, value);
        }
        response.body.pipe(res);
        response.body.on('error', (error) => {
          res.status(500).json(error);
        });
      } catch (error) {
        res
          .status(500)
          .json({ messsage: 'Error processing endpoint', errorMessage: error.message, stack: error.stack });
        console.error(error);
        console.error('Error processing proxy endpoint');
      }
    };
  }

  function getServiceInfo(serviceInfo) {
    let token = undefined,
      uri = null;
    if (typeof serviceInfo === 'string') {
      uri = serviceInfo;
    } else if (typeof serviceInfo === 'object' && serviceInfo != null) {
      ({ uri, token } = serviceInfo);
    } else {
      throw new Error('Invalid service info param');
    }
    if (uri == null) {
      throw new Error('Empty uri for service');
    }
    return { token, uri };
  }

  function serviceCall({ path, schema, method }, { auth = {}, token }, ajv) {
    const httpMethod = getMethod(method);

    return async (body = {}, options = {}) => {
      if (schema != null) {
        const valid = ajv.validate(schema, body);
        if (!valid) throw new Ajv.ValidationError(ajv.errors);
      }

      //retrieve internal API token dynamically if possible
      if (token && dynamicMethods.getInternalAPIToken()) {
        token = dynamicMethods.getInternalAPIToken();
      }

      options.headers = { ...(options.headers || {}), ...(auth.headers || {}), authorization: token };
      dynamicMethods.addCustomHeaders(options.headers);
      if (body != null && typeof body === 'object') options.body = JSON.stringify(body);
      const url = formatUrl(path, options);
      return httpMethod(url, {
        credentials: 'include',
        ...options,
      });
    };
  }

  function formatUrl(path, { params = {}, query = {} }) {
    const generator = pathToRegexp.compile(path, { encode: encodeURIComponent });
    const search = isEmpty(query) ? '' : '?' + querystring.stringify(query);
    return `${uri}${generator(params)}${search}`;
  }
};

const dynamicMethods = {
  addCustomHeaders: (headers) => {
    return headers;
  },
  getInternalAPIToken: () => {
    return null;
  },
};

module.exports.dynamicMethods = dynamicMethods;

function getTimeoutOption() {
  if ((global.location && global.location.href) || !AbortSignal) {
    return {};
  }
  return {
    signal: AbortSignal.timeout(30000),
  };
}

function getJSON(path, options) {
  delete options.body;
  return fetch(path, { ...options, ...getTimeoutOption() }).then(checkStatus);
}

function postJSON(path, options) {
  return bodyRequest(path, { ...options, ...getTimeoutOption() }, 'POST');
}

function putJSON(path, options) {
  return bodyRequest(path, { ...options, ...getTimeoutOption() }, 'PUT');
}

function patchJSON(path, options) {
  return bodyRequest(path, { ...options, ...getTimeoutOption() }, 'PATCH');
}

function _delete(path, options) {
  return fetch(path, { ...options, method: 'DELETE', ...getTimeoutOption() }).then(checkStatus);
}

function bodyRequest(path, options, method) {
  const { headers = {}, ...rest } = options;
  return fetch(path, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
    ...rest,
  }).then(checkStatus);
}

async function getResponseError(res) {
  const body = await res.json();
  if (body.type != null) {
    const error = new ResponseError(body.title, body.details, body.type, res.status);
    Object.assign(error, body);
    return error;
  }
  return null;
}

function getMetadata(res) {
  let totalCount = res.headers.get('X-Total-Count');
  if (totalCount != null) {
    totalCount = parseInt(totalCount);
  }
  const link = res.headers.get('Link');
  const cookie = getCookieHeaderValue(res);
  const pagination = totalCount != null && link != null ? { totalCount, link } : null;
  return { pagination, cookie };
}

function getCookieHeaderValue(res) {
  if (res.headers != null && res.headers._headers != null) {
    return res.headers._headers['set-cookie'];
  }
  if (res.headers != null && res.headers.get instanceof Function) {
    const setCookieValues = res.headers.get('set-cookie');
    const cookies = [];
    if (setCookieValues != null) {
      const regex = /(.*)(,)\s*([^=]+=[^;]*;?)/g;
      let result = null;
      let lastNextCookie = null;
      while ((result = regex.exec(setCookieValues)) != null) {
        const [, cookieString, , nextCookieValue] = result;
        lastNextCookie = nextCookieValue;
        cookies.push(cookieString);
      }
      cookies.push(setCookieValues.slice(setCookieValues.lastIndexOf(lastNextCookie)));

      return cookies;
    }
  }
}

const redirectToLogin = (code) => {
  const { hostname, protocol } = global.location;

  if (hostname === 'localhost') {
    return `http://my.local.tradetrax.com`;
  }

  if (global.location.href.includes('//my')) return;

  const hostUrl = hostname.replace(
    /^(dev-)?([^.]+)((?:.*)?.tradetrax\.com)$/,
    (m, prefix = '', service, host) => `${protocol}//${prefix}my${host}`,
  );

  const continueURL = global.location;
  const timeout = '1';
  const url = `${hostUrl}/login`;
  const redirect = querystring.stringifyUrl({ url, query: { continueURL, timeout, sessionErrorCode: code } });
  global.location.replace(redirect);
};

async function checkStatus(res) {
  if (res.ok) {
    const responseBodyPromise = res.status === 204 ? Promise.resolve({}) : res.json();
    const responseBody = await responseBodyPromise;
    Object.defineProperty(responseBody, 'metadata', {
      value: getMetadata(res),
      enumerable: false,
    });
    return responseBody;
  }
  const resCopy = res.clone();
  const error = await getResponseError(res);
  if (error != null) {
    if (error.httpCode === 401 && global.location && global.location.href) {
      const code = await getUnAuthoriziedCode(resCopy);
      redirectToLogin(code);
    }
    throw error;
  }
  throw new Error(res.statusText);
}

async function getUnAuthoriziedCode(res) {
  let code = 'no-code';
  try {
    const body = await res.json();
    code = body.code;
  } catch (error) {
    console.error(error);
    console.error('Could not get body from Unauthorized response');
  }
  return code;
}
